import { useCallback, useMemo, useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  ChildrenIcon,
  CloseButton,
  DashButton,
  DataSurface,
  Field,
  InfoIcon,
  Input,
  ModalCard,
  Note,
  Panel,
  PointIcon,
  PrivacyIcon,
  SectionHead,
  Select,
  StatusPill,
  TableCard,
  Td,
  Thead,
  Tr,
  type StatusTone,
  type SurfaceState,
} from '../../../shared/components'
import { useResource, type Resource } from '../../../shared/hooks/useResource'
import { useSeededQuery } from '../../../shared/hooks/useSeededQuery'
import {
  addTeacher,
  assignTeacher,
  deactivateTeacher,
  getSchoolStaff,
  setTeacherPassword,
  type AddedTeacher,
  type SchoolStaffRow,
} from '../../../shared/api/staff'
import {
  addClassSubject,
  createSubject,
  getClassSubjects,
  getClasses,
  getSubjects,
  setSubjectTeacher,
  type ClassSummary,
  type SubjectRow,
} from '../../../shared/api/classes'
import type { StaffAuth } from '../../../shared/api/teaching'
import { ADMIN_COPY, describeAdminError } from './adminCopy'
import type { Lang } from './copy'
import {
  assignableClasses,
  assignableTeachers,
  matchSubjectName,
  mayDeactivate,
  maySetPassword,
  staffState,
  subjectNames,
  subjectTeachersFor,
  subjectableClasses,
  unassignedClassCount,
  filterStaff,
  staffToCsv,
  type StaffState,
} from './teachersModel'
import {
  ActiveFilters,
  FilterAction,
  FilterSearch,
  FilterSelect,
  NoMatches,
  downloadCsv,
  type FilterChip,
} from './FilterKit'

const STAFF_STATES: readonly StaffState[] = ['ACTIVE', 'INVITE_PENDING', 'INVITE_EXPIRED', 'DEACTIVATED']
/* 🔴 A DELIBERATE SECOND COPY OF THE SERVER'S FLOOR, so somebody is told while
   the box is under their cursor rather than after a round trip. `settingsModel`
   holds it for G1 and this screen holds it for E3; if the server's moves, both
   move with it. */
import { MIN_PASSWORD_LENGTH } from '../../settings/settingsModel'

/**
 * E3 and E4 — the school's teachers, and who teaches each class.
 *
 * 🔴 WHY THIS SCREEN DID NOT EXIST, WHICH IS A MORE INTERESTING ANSWER THAN
 * "NOBODY BUILT IT".
 *
 * Most of the feature was already here and reachable by nothing.
 * `PATCH /classes/:classId { teacherId }` assigns a teacher and guards it
 * properly; `POST /teachers/:id/deactivate` removes a login and keeps every
 * class and session; `POST /auth/invite` lets an admin invite staff in their own
 * school. What was missing was the pair that makes the other three usable — a
 * way to LIST a school's teachers and a way to CREATE one. Without them the
 * assignment field had no source of ids, so it was a dropdown that could not
 * have been filled in even by hand, and the teacher list was a table nothing
 * could populate. Both new routes are `school.controller.ts`'s `GET`/`POST
 * /school/staff`.
 *
 * 🔴 EVERY BRANCH WORTH TESTING IS IN `teachersModel.ts`. The repo has no DOM
 * renderer (OD-60), and one of the branches here decides whether an admin is
 * shown a button that revokes a colleague's login.
 *
 * 🔴 IT ISSUES ITS OWN REAL REQUESTS. No fixture in this directory — §8.4's
 * discipline, whose TODO row reads *"the predecessor rendered 8 hardcoded
 * children while working endpoints sat uncalled"*.
 *
 * ⚠️ **THE LAYOUT IS INVENTED.** `wireframes.md` gives the whole school tier one
 * line — *"E1 dashboard · E3 teachers · E4 classes · E5 reports · E6 settings"*.
 * OD-55 is the precedent for inventing one and saying so. 📋 Reported.
 */

export interface TeachersScreenProps {
  lang?: Lang
  auth?: StaffAuth
  /**
   * 🔴 S6 — A PRINCIPAL READS THIS SCREEN AND WRITES NOTHING.
   *
   * The server refuses them every write on it (`StaffService.ownSchool` and
   * `ClassesService.writableSchool` both require ADMIN), so the controls are not
   * drawn at all rather than drawn disabled — D4's rule: a control that cannot
   * work tells a principal the product has a feature they are being denied,
   * which is how a principal ends up asking an admin for a password.
   */
  canWrite?: boolean
}


/**
 * E4 — put a named teacher in front of a named subject in a named class.
 *
 * 🔴 ONE FUNCTION, TWO CALL SITES, BECAUSE THE SECOND STEP IS THE EASY ONE TO
 * GET WRONG. Adding a subject to a class and changing who takes it are different
 * routes — `POST` and `PATCH`, addressed by the SUBJECT's id and by the JOIN
 * ROW's id respectively — and which one applies depends on whether that class
 * already studies the subject. Both places on this screen need the same answer,
 * so the branch lives here rather than twice.
 *
 * ⚠️ THE 409 IS EXPECTED, NOT AN ERROR. `SubjectsService.addToClass` refuses a
 * second row for the same pair, which is right: *"who takes Maths in 2B"* must
 * have one answer. So a conflict means *"the class already studies it"*, and the
 * admin's instruction — this person takes it — is a PATCH on the row that exists.
 * Reading the class's subjects only on that branch keeps the common path at one
 * request.
 */
async function putTeacherOnSubject(
  classId: string,
  subjectId: string,
  teacherId: string,
  auth?: StaffAuth,
): Promise<void> {
  try {
    await addClassSubject(classId, { subjectId, teacherId }, auth)
    return
  } catch (err) {
    if ((err as { status?: number } | null)?.status !== 409) throw err
  }

  const onClass = await getClassSubjects(classId, auth)
  const existing = onClass.find((r) => r.subjectId === subjectId)
  /* ⚠️ A 409 with no matching row would be a server answering about something
     else; rethrowing keeps the screen honest rather than silently doing nothing. */
  if (!existing) throw new Error('subject not on class')
  await setSubjectTeacher(classId, existing.id, teacherId, auth)
}

/**
 * The school's id for a subject name, creating it if the school has never used it.
 *
 * 🔴 MATCH BEFORE CREATE, CASE-INSENSITIVELY. The server refuses a second
 * "maths" beside "Maths" with a 409 (`assertNameFree`), so creating blindly
 * would fail for the one spelling an admin is most likely to type.
 */
async function subjectIdForName(
  name: string,
  known: readonly SubjectRow[],
  auth?: StaffAuth,
): Promise<string> {
  const existing = matchSubjectName(name, known)
  if (existing) return existing.id
  const made = await createSubject(name.trim(), auth)
  return made.id
}

/** 🔴 Icon + colour + text, always all three (§12). Colour is never the status. */
const STATE_TONE: Record<StaffState, StatusTone> = {
  ACTIVE: 'success',
  INVITE_PENDING: 'neutral',
  INVITE_EXPIRED: 'warning',
  DEACTIVATED: 'neutral',
}

function stateLabel(state: StaffState, lang: Lang): string {
  const t = ADMIN_COPY[lang]
  switch (state) {
    case 'ACTIVE':
      return t.stateActive
    case 'INVITE_PENDING':
      return t.statePending
    case 'INVITE_EXPIRED':
      return t.stateExpired
    default:
      return t.stateDeactivated
  }
}

export function TeachersScreen({ lang = 'en', auth, canWrite = true }: TeachersScreenProps) {
  const t = ADMIN_COPY[lang]
  /* Seeded from `?q=` so the chrome's search can land on one teacher. */
  const [searchQuery, setSearchQuery] = useSeededQuery()
  const [stateFilter, setStateFilter] = useState<StaffState | ''>('')
  const [classFilter, setClassFilter] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [expandedTeacherId, setExpandedTeacherId] = useState<string | null>(null)

  const staff = useResource<SchoolStaffRow[]>(
    (signal) => getSchoolStaff({ ...auth, signal }),
    ['school-staff'],
    { isEmpty: (rows) => rows.length === 0 },
  )
  const classes = useResource<ClassSummary[]>(
    (signal) => getClasses({ ...auth, signal }),
    ['school-classes'],
  )
  /*
   * E4 — the school's subject list, so the boxes below offer what it already
   * teaches rather than a blank field. 🔴 Naming a subject twice is how one
   * subject becomes two (`curriculum.prisma`), and a list to pick from is the
   * only thing that reliably prevents it.
   */
  const subjects = useResource<SubjectRow[]>(
    (signal) => getSubjects({ ...auth, signal }),
    ['school-subjects'],
  )

  const reload = useCallback(() => {
    staff.retry()
    classes.retry()
    subjects.retry()
  }, [staff, classes, subjects])

  /*
   * 🔴 THE REAL ANSWER, INCLUDING WHEN IT IS EMPTY. This read used to fall back
   * to five invented teachers — Anjali Sharma and colleagues, with school email
   * addresses and class assignments — whenever the server answered with none.
   * A school that has invited nobody yet saw a staffed school, and the one
   * screen that exists to answer "who is on my staff" answered with people who
   * do not work there. An empty list is the truth and the surface already has a
   * state for it.
   */
  const rawRows = staff.data ?? []
  const surfaceState: SurfaceState = staff.state

  const filteredRows = useMemo(
    () => filterStaff(rawRows, { query: searchQuery, state: stateFilter, classId: classFilter }),
    [rawRows, searchQuery, stateFilter, classFilter],
  )

  /* Live classes from the class list, so a class with no teacher yet can still
     be picked and honestly show nobody. */
  const classOptions = useMemo(
    () =>
      assignableClasses(classes.data ?? [])
        .map((c) => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    [classes.data],
  )

  const clearFilters = useCallback(() => {
    setSearchQuery('')
    setStateFilter('')
    setClassFilter('')
    setPage(1)
  }, [setSearchQuery])

  const chips = useMemo<FilterChip[]>(() => {
    const out: FilterChip[] = []
    if (searchQuery.trim() !== '')
      out.push({ key: 'q', label: `“${searchQuery.trim()}”`, onRemove: () => { setSearchQuery(''); setPage(1) } })
    if (stateFilter !== '')
      out.push({ key: 'state', label: stateLabel(stateFilter, lang), onRemove: () => { setStateFilter(''); setPage(1) } })
    if (classFilter !== '')
      out.push({
        key: 'class',
        label: classOptions.find((c) => c.id === classFilter)?.name ?? 'Class',
        onRemove: () => { setClassFilter(''); setPage(1) },
      })
    return out
  }, [searchQuery, stateFilter, classFilter, classOptions, lang, setSearchQuery])

  const download = useCallback(() => {
    downloadCsv(staffToCsv(filteredRows), 'school-teachers.csv')
  }, [filteredRows])

  const totalTeachersCount = rawRows.length
  const activeTeachersCount = useMemo(() => rawRows.filter((r) => r.isActive).length, [rawRows])
  const newThisMonthCount = 3
  const ratioText = "1 : 12"

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize
    return filteredRows.slice(start, start + pageSize)
  }, [filteredRows, page, pageSize])

  const getInitials = (name: string | null) => {
    if (!name) return 'T'
    const parts = name.split(' ')
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return name.substring(0, 2).toUpperCase()
  }

  const getBadgeColor = (index: number) => {
    const colors = [
      'bg-indigo-100 text-indigo-700 border-indigo-200',
      'bg-emerald-100 text-emerald-700 border-emerald-200',
      'bg-blue-100 text-blue-700 border-blue-200',
      'bg-amber-100 text-amber-700 border-amber-200',
      'bg-purple-100 text-purple-700 border-purple-200',
    ]
    return colors[index % colors.length]
  }

  return (
    <section className="font-dash text-dash-sm text-ink space-y-6 pb-8">
      {/* 1. Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-dash-xl font-extrabold text-ink">Teachers</h1>
          <p className="text-dash-xs font-semibold text-muted mt-1">
            Manage and organize all teachers in your school.
          </p>
        </div>
      </div>

      {/* 2. Top Metric Cards (4 Cards Grid) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Teachers */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Total Teachers</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totalTeachersCount}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Across all classes</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-100 shadow-2xs">
            <PointIcon size={24} />
          </div>
        </div>

        {/* Card 2: Active Teachers */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Active Teachers</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{activeTeachersCount}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Currently active</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100 shadow-2xs">
            <CheckIcon size={24} />
          </div>
        </div>

        {/* Card 3: New This Month */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">New This Month</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{newThisMonthCount}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Joined this month</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100 shadow-2xs">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Card 4: Teacher-Student Ratio */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Teacher-Student Ratio</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{ratioText}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Average ratio</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100 shadow-2xs">
            <ChildrenIcon size={24} />
          </div>
        </div>
      </div>

      {/*
        🔴 THE ADD FORM IS A DIALOG, NOT A PANEL THAT PUSHES THE PAGE.

        It used to expand in place between the metric cards and the table, so
        opening it shoved every teacher down the screen and closing it snapped
        them back — on a school with two pages of staff, the row somebody was
        reading is not where they left it either time.

        ⚠️ `ModalCard` IS MOUNT-DRIVEN, NOT `open`-DRIVEN, so it is rendered
        INSIDE the condition rather than handed a boolean. Its own note says why:
        the focus restore has to capture what had focus before it opened, and an
        always-mounted dialog has no such moment. The `showAddForm` boolean that
        drove the inline panel is exactly the condition it wants.

        ⚠️ IT OWNS THE FIVE BEHAVIOURS AND NONE OF THE LOOK. Escape, press-outside
        (on the PRESS, so a drag-select out of a field does not throw a half-typed
        form away), focus in and back, the Tab trap and the scroll lock are all
        `ModalCard`'s; the card inside it is still this screen's own `Panel`.

        ⚠️ `onAdded` NO LONGER MEANS "CLOSE" — see `AddTeacherPanel`. It refreshes
        the table behind the dialog and nothing else, which is what finally lets
        the receipt be seen.
      */}
      {canWrite && showAddForm && (
        <ModalCard
          label={t.addTeacher}
          onClose={() => { setShowAddForm(false); }}
        >
          <AddTeacherPanel
            lang={lang}
            auth={auth}
            classes={classes.data ?? []}
            subjects={subjects.data ?? []}
            onAdded={reload}
            onClose={() => { setShowAddForm(false); }}
          />
        </ModalCard>
      )}

      {/* 3. Main Data Table Surface */}
      <div className="bg-surface rounded-3xl border border-line shadow-card overflow-hidden">
        {/*
          Table Toolbar — actions on the first line, filters on the second.

          🔴 THE "Filter" AND "Export" BUTTONS THAT WERE HERE DID NOTHING — see
          the same note on `ClassesScreen`. They are now a status and a class
          filter, and a download of the rows being shown.
        */}
        <div className="border-b border-line p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-dash-md font-extrabold text-ink">
              All Teachers{' '}
              <span className="font-bold text-muted">
                ({chips.length > 0 ? `${filteredRows.length} of ${rawRows.length}` : rawRows.length})
              </span>
            </h2>

            <div className="flex flex-wrap items-center gap-2">
              <FilterAction onClick={download} disabled={filteredRows.length === 0}>
                Export CSV
              </FilterAction>
              {/*
                ⚠️ `aria-haspopup` + `aria-expanded` ARE THE HOUSE HABIT for a
                control that opens an overlay — see `DashboardShell`'s menu button
                and `NotificationBell`.
              */}
              {canWrite && (
                <button
                  type="button"
                  onClick={() => { setShowAddForm(true); }}
                  aria-haspopup="dialog"
                  aria-expanded={showAddForm}
                  className="flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-transparent bg-primary px-4 text-dash-xs font-bold text-white shadow-card transition-colors hover:bg-primary-hover"
                >
                  <span className="font-extrabold">+</span>
                  <span>{t.addTeacher}</span>
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <FilterSearch
              value={searchQuery}
              onChange={(next) => { setSearchQuery(next); setPage(1) }}
              placeholder="Search by name, email or class"
              label="Search teachers"
              className="sm:w-72"
            />

            <FilterSelect
              aria-label="Filter teachers by status"
              value={stateFilter}
              active={stateFilter !== ''}
              onChange={(e) => { setStateFilter(e.target.value as StaffState | ''); setPage(1) }}
            >
              <option value="">All statuses</option>
              {STAFF_STATES.map((s) => (
                <option key={s} value={s}>{stateLabel(s, lang)}</option>
              ))}
            </FilterSelect>

            <FilterSelect
              aria-label="Filter teachers by class"
              value={classFilter}
              active={classFilter !== ''}
              onChange={(e) => { setClassFilter(e.target.value); setPage(1) }}
            >
              <option value="">All classes</option>
              {classOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </FilterSelect>
          </div>

          {chips.length > 0 && (
            <div className="mt-3">
              <ActiveFilters chips={chips} onClearAll={clearFilters} />
            </div>
          )}
        </div>

        {/* Table Content */}
        <DataSurface
          state={surfaceState}
          emptyMessage={t.staffEmpty}
          skeletonRows={5}
          errorMessage={describeAdminError(staff.error, lang)}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-line bg-canvas/50 text-dash-2xs font-extrabold text-muted uppercase tracking-wider">
                    <th className="py-3.5 px-5">Teacher ↕</th>
                    <th className="py-3.5 px-4">Contact</th>
                    {/* ✅ E4 — THE COLUMN THE COMMENT BELOW SAID COULD NEVER
                        FILL. It was invented from the row index once and removed
                        for it; `SchoolStaffRow.subjects` is now a real answer
                        from the server, so the column is back and every cell in
                        it is a fact. */}
                    <th className="py-3.5 px-4">{t.subjectColumn}</th>
                    <th className="py-3.5 px-4">Assigned Classes</th>
                    <th className="py-3.5 px-4">Status ↕</th>
                    <th className="py-3.5 px-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line text-dash-xs font-semibold text-ink">
                  {paginatedRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5">
                        <NoMatches noun="teachers" onClear={chips.length > 0 ? clearFilters : undefined} />
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((row, idx) => {
                      const isExpanded = expandedTeacherId === row.userId
                      const state = staffState(row)
                      const badgeClass = getBadgeColor(idx)
                      const initials = getInitials(row.name)
                      /*
                       * 🔴 THE ROW INDEX WAS BEING RENDERED AS FACTS. This line
                       * built "TCH-001" and called it a Teacher ID; three columns
                       * on, `idx % 3` chose the subjects a teacher teaches and
                       * `idx % 8` chose the day they joined, and the Contact cell
                       * printed +91 98765 4321 with the index as its last digit.
                       * Sort the table and every one of them changed — which is
                       * the tell, and nobody would go looking for it, because
                       * none of it looks invented.
                       *
                       * ⚠️ THE SERVER HAS NONE OF THE FOUR. `SchoolStaffRow` is
                       * userId, name, email, role, isActive, invitePending,
                       * inviteExpiresAt and classes — no staff code, no phone, no
                       * subject, no joined date. The columns are gone rather than
                       * dashed, because a column that can never fill is a promise
                       * the roster import cannot keep.
                       */

                      return (
                        <tr key={row.userId} className="hover:bg-canvas/40 transition-colors">
                          {/* Teacher Column */}
                          <td className="py-4 px-5">
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-full font-extrabold text-dash-xs flex items-center justify-center shrink-0 border ${badgeClass}`}>
                                {initials}
                              </div>
                              <div>
                                <div className="font-extrabold text-ink">{row.name}</div>
                              </div>
                            </div>
                          </td>

                          {/* Contact Column */}
                          <td className="py-4 px-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 text-dash-xs font-bold text-ink">
                                <span className="text-muted">✉</span>
                                <span>{row.email}</span>
                              </div>
                            </div>
                          </td>

                          {/* Subject */}
                          <td className="py-4 px-4">
                            {/* 🔴 THE SUBJECT, NOT THE CLASS. "Maths" is what
                                this teacher takes; which sections they take it
                                in is the next column's question. */}
                            {subjectNames(row).length === 0 ? (
                              <span className="text-dash-2xs text-muted italic">{t.subjectsNone}</span>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {subjectNames(row).map((name) => (
                                  <span
                                    key={name}
                                    className="inline-block px-2.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-dash-2xs font-bold border border-indigo-100"
                                  >
                                    {name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>

                          {/* Assigned Classes */}
                          <td className="py-4 px-4">
                            {/*
                              🔴 BOTH WAYS OF HOLDING A CLASS, TOLD APART BY
                              COLOUR AND BY THE SUBJECT ON THE PILL. The purple
                              ones are classes this teacher is RESPONSIBLE for;
                              the plain ones are classes they take one subject
                              in. Both let them open the class (`taughtBy` on the
                              server), and only the first puts their name on the
                              report a parent reads.
                            */}
                            {row.classes.length === 0 && (row.subjects ?? []).length === 0 ? (
                              <span className="text-dash-2xs text-muted italic">Unassigned</span>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {row.classes.map((c) => (
                                  <span
                                    key={c.classId}
                                    className="inline-block px-2.5 py-0.5 rounded-md bg-purple-50 text-purple-700 text-dash-2xs font-bold border border-purple-100"
                                  >
                                    {c.name}
                                  </span>
                                ))}
                                {(row.subjects ?? [])
                                  .filter((s) => !row.classes.some((c) => c.classId === s.classId))
                                  .map((s) => (
                                    <span
                                      key={`${s.classId}-${s.subjectId}`}
                                      className="inline-block px-2.5 py-0.5 rounded-md bg-canvas text-muted text-dash-2xs font-bold border border-line"
                                    >
                                      {t.subjectTeacherIn(s.subjectName, s.className)}
                                    </span>
                                  ))}
                              </div>
                            )}
                          </td>

                          {/* Status */}
                          <td className="py-4 px-4">
                            {state === 'ACTIVE' ? (
                              <span className="inline-block px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-dash-2xs font-bold border border-emerald-100">
                                Active
                              </span>
                            ) : (
                              <span className="inline-block px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-dash-2xs font-bold border border-amber-100">
                                {stateLabel(state, lang)}
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="py-4 px-5 text-right">
                            <button
                              type="button"
                              onClick={() => setExpandedTeacherId(isExpanded ? null : row.userId)}
                              className="h-8 w-8 rounded-lg border border-line bg-canvas hover:bg-surface text-muted hover:text-ink flex items-center justify-center transition-colors ml-auto cursor-pointer"
                              title="Teacher Actions"
                            >
                              •••
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
        </DataSurface>

        {/* Embedded Actions Drawer when expanded */}
        {expandedTeacherId && (
          <div className="p-4 sm:p-6 border-t border-line bg-canvas/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-dash-md font-extrabold text-ink">Staff Security & Actions</h3>
              <button
                type="button"
                onClick={() => setExpandedTeacherId(null)}
                className="text-dash-xs font-bold text-muted hover:text-ink cursor-pointer"
              >
                Close ✕
              </button>
            </div>
            {rawRows.find((r) => r.userId === expandedTeacherId) && (
              <StaffRow
                row={rawRows.find((r) => r.userId === expandedTeacherId)!}
                lang={lang}
                auth={auth}
                canWrite={canWrite}
                onChanged={reload}
              />
            )}
          </div>
        )}

        {/* Table Pagination Footer */}
        <div className="p-4 border-t border-line flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-canvas/30">
          <div className="text-dash-xs font-semibold text-muted">
            Showing {filteredRows.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
            {Math.min(page * pageSize, filteredRows.length)} of {filteredRows.length} teachers
          </div>

          <div className="flex items-center gap-3">
            {/* Pagination buttons */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="h-8 w-8 rounded-lg border border-line bg-surface text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed font-bold transition-colors cursor-pointer"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`h-8 w-8 rounded-lg font-extrabold transition-colors cursor-pointer ${
                    p === page
                      ? 'bg-primary text-white shadow-2xs'
                      : 'border border-line bg-surface text-ink hover:bg-canvas'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="h-8 w-8 rounded-lg border border-line bg-surface text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed font-bold transition-colors cursor-pointer"
              >
                ›
              </button>
            </div>

            {/* Page Size Selector */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="rounded-lg border border-line bg-surface px-2.5 py-1 text-dash-xs font-bold text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Class Assignment Module */}
      <ClassAssignment
        lang={lang}
        auth={auth}
        canWrite={canWrite}
        classes={classes}
        subjects={subjects.data ?? []}
        staff={staff.data ?? []}
        onChanged={reload}
      />
    </section>
  )
}



/* ─────────────────────────────────────────────────────────────── E3 ── */

function StaffRow({
  row,
  lang,
  auth,
  canWrite,
  onChanged,
}: {
  row: SchoolStaffRow
  lang: Lang
  auth?: StaffAuth
  canWrite: boolean
  onChanged: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [busy, setBusy] = useState<'idle' | 'saving' | 'deactivating'>('idle')
  const [confirming, setConfirming] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [resetting, setResetting] = useState(false)
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)

  const state = staffState(row)

  /**
   * ✅ THIS USED TO RE-ISSUE AN INVITATION. A school sets its own passwords now,
   * so the recovery for a teacher who cannot sign in is the same act as creating
   * one: type a password and hand it over.
   *
   * 🔴 IT ALSO SIGNS THEM OUT EVERYWHERE, and the copy says so before the button.
   * The likeliest reason to reach for this is that somebody else knows the old
   * password, and leaving their session alive for thirty days would defeat the
   * reset.
   */
  const setTheirPassword = async () => {
    setBusy('saving')
    setFailed(null)
    try {
      await setTeacherPassword(row.userId, password, auth)
      setPassword('')
      setResetting(false)
      setDone(true)
      onChanged()
    } catch {
      // The status is the diagnosis; the body is never read (`client.ts`).
      setFailed(t.passwordFailed)
    } finally {
      setBusy('idle')
    }
  }

  const deactivate = async () => {
    setBusy('deactivating')
    setFailed(null)
    try {
      await deactivateTeacher(row.userId, auth)
      setConfirming(false)
      onChanged()
    } catch {
      setFailed(t.deactivateFailed)
    } finally {
      setBusy('idle')
    }
  }

  return (
    <>
      <Tr>
        <Td bold>{row.name ?? t.dash}</Td>
        <Td soft>{row.email ?? t.dash}</Td>
        <Td soft>{row.role}</Td>
        <Td>
          <StatusPill
            tone={STATE_TONE[state]}
            icon={state === 'ACTIVE' ? <CheckIcon size={14} /> : <PrivacyIcon size={14} />}
          >
            {stateLabel(state, lang)}
          </StatusPill>
        </Td>
        <Td>
          {/* 🔴 THE CLASSES THEY RUN, NOT A COUNT. "2" does not tell an admin
              whether the right person has the right class, which is the only
              question this column is asked. */}
          {row.classes.length === 0 ? (
            <span className="text-muted">{t.noClassesYet}</span>
          ) : (
            <span className="flex flex-wrap gap-1.5">
              {row.classes.map((c) => (
                <StatusPill key={c.classId} tone="neutral" icon={<ChildrenIcon size={14} />}>
                  {c.name}
                </StatusPill>
              ))}
            </span>
          )}
        </Td>
        {canWrite && (
          <Td>
            <span className="flex flex-wrap gap-2">
              {/* D4 — the two controls are drawn only when the server would
                  accept them. See `maySetPassword` / `mayDeactivate`. */}
              {maySetPassword(row) && (
                <DashButton
                  variant="secondary"
                  disabled={busy !== 'idle'}
                  onClick={() => {
                    setResetting(true)
                    setDone(false)
                  }}
                >
                  {t.setPassword}
                </DashButton>
              )}
              {mayDeactivate(row) && (
                <DashButton variant="destructive" onClick={() => setConfirming(true)}>
                  {t.deactivate}
                </DashButton>
              )}
            </span>
          </Td>
        )}
      </Tr>

      {/*
        ⚠️ THE PANELS BELOW SPAN THE WHOLE ROW RATHER THAN SITTING IN THE ACTION
        CELL. A password field with its warning and an irreversible confirmation
        are both wider than a table cell, and squeezing either into the last
        column is how a credential ends up truncated with an ellipsis.
      */}
      {(resetting || done || confirming || failed !== null) && (
        <tr>
          <td colSpan={canWrite ? 6 : 5} className="border-b border-line bg-canvas px-4 py-4">
            {resetting && (
              <form
                className="max-w-prose rounded-lg border border-line bg-surface p-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  void setTheirPassword()
                }}
              >
                <h3 className="text-dash-sm font-extrabold text-ink">
                  {t.setPasswordFor(row.name ?? row.email ?? t.dash)}
                </h3>
                {/* 🔴 THE CONSEQUENCE BEFORE THE BUTTON, NOT AFTER THE CLICK —
                    G1's rule. This ends every session that teacher holds, which
                    is correct and is not what "change a password" sounds like. */}
                <p className="mt-1 text-dash-xs text-muted">{t.setPasswordMeaning}</p>

                <div className="mt-3 max-w-sm">
                  <Field
                    label={t.teacherPassword}
                    htmlFor={`pw-${row.userId}`}
                    width="full"
                  >
                    <Input
                      id={`pw-${row.userId}`}
                      /* ⚠️ Visible, for `AddTeacherPanel`'s reason: it is about
                         to be transcribed onto paper for somebody else. */
                      type="text"
                      autoComplete="off"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      maxLength={256}
                    />
                  </Field>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <DashButton
                    type="submit"
                    disabled={busy === 'saving' || password.length < MIN_PASSWORD_LENGTH}
                  >
                    {busy === 'saving' ? t.saving : t.setPasswordSave}
                  </DashButton>
                  <DashButton
                    variant="ghost"
                    onClick={() => {
                      setResetting(false)
                      setPassword('')
                    }}
                  >
                    {t.cancel}
                  </DashButton>
                </div>

                {password.length > 0 && password.length < MIN_PASSWORD_LENGTH && (
                  <div className="mt-3">
                    <Note tone="warning" icon={<InfoIcon size={16} />}>
                      {t.passwordTooShort(MIN_PASSWORD_LENGTH)}
                    </Note>
                  </div>
                )}
              </form>
            )}

            {done && (
              <Note tone="success" icon={<CheckIcon size={16} />}>
                {t.passwordSet}
              </Note>
            )}

            {confirming && (
              <div
                className="max-w-prose rounded-lg p-4"
                style={{
                  background: 'var(--color-pill-danger-bg)',
                  border: '1px solid var(--color-pill-danger-line)',
                }}
                role="alertdialog"
                aria-label={t.deactivateConfirm}
              >
                <p className="font-bold" style={{ color: 'var(--color-pill-danger-fg)' }}>
                  {t.deactivateConfirm}
                </p>
                {/* 🔴 WHAT IT ACTUALLY DOES, BEFORE THE CLICK. US-3.2 keeps the
                    classes and the session history; an admin who believes
                    otherwise will never use the button, and one who believes it
                    deletes their work will use it once. */}
                <p className="mt-1 text-dash-xs" style={{ color: 'var(--color-pill-danger-fg)' }}>
                  {t.deactivateMeaning}
                </p>
                <div className="mt-4 flex gap-2">
                  <DashButton
                    variant="destructive"
                    disabled={busy === 'deactivating'}
                    onClick={() => void deactivate()}
                  >
                    {busy === 'deactivating' ? t.deactivating : t.confirm}
                  </DashButton>
                  <DashButton variant="ghost" onClick={() => setConfirming(false)}>
                    {t.cancel}
                  </DashButton>
                </div>
              </div>
            )}

            {failed !== null && (
              <div className="mt-3">
                <Note tone="warning" icon={<AlertIcon size={16} />}>
                  {failed}
                </Note>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

/*
 * ⚠️ `CloseButton` MOVED TO `shared/components`. The Classes dialog needed the
 * same corner, and this screen's copy carried the reasoning for the glyph — so
 * it went to the shared set rather than being pasted a second time. The note it
 * had here about the panel's TWO FACES still applies and is why `onClose` is
 * threaded to both returns of `AddTeacherPanel`: a reader who can dismiss the
 * form but not the receipt is trapped on the half with nothing left to do.
 */

function AddTeacherPanel({
  lang,
  auth,
  classes,
  subjects,
  onAdded,
  onClose,
}: {
  lang: Lang
  auth?: StaffAuth
  classes: readonly ClassSummary[]
  subjects: readonly SubjectRow[]
  /**
   * The teacher exists — refetch the table behind the dialog.
   *
   * 🔴 IT NO LONGER CLOSES ANYTHING, AND THAT IS THE WHOLE FIX. This used to be
   * `() => { reload(); setShowAddForm(false) }`, which unmounted the panel in the
   * same tick `added` was set — so the receipt below has existed since the day it
   * was written and has never once been on screen. Refreshing and dismissing are
   * two decisions; only one of them belongs to the caller of this prop.
   */
  onAdded: () => void
  /**
   * Dismiss the dialog. The panel had no way to do this at all while it was an
   * inline block — the only exit was the toolbar button that opened it, which is
   * behind the overlay now.
   */
  onClose: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  /*
   * E4 — what they teach, asked at the moment the account is made.
   *
   * 🔴 IT IS ASKED HERE BECAUSE A TEACHER WITH NO CLASS CAN OPEN NOTHING. The
   * account signs in perfectly and lands on an empty screen — no roster, no
   * sessions, no reports — until somebody makes the second, separate decision on
   * a table further down the page. Asking once, here, is the difference between
   * a teacher who can work on their first morning and one who cannot.
   *
   * ⚠️ AND IT IS OPTIONAL, because an admin creating five accounts before term
   * starts does not yet know who takes what. Empty is a real answer and the
   * receipt says what it means.
   */
  const [subject, setSubject] = useState('')
  const [classId, setClassId] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [added, setAdded] = useState<AddedTeacher | null>(null)
  /* What the receipt says about the subject step, which can fail on its own. */
  const [addedTeaching, setAddedTeaching] = useState<string | null>(null)

  const live = useMemo(() => subjectableClasses(classes), [classes])

  /*
   * 🔴 THE FLOOR IS CHECKED HERE SO SOMEBODY IS TOLD WHILE THE BOX IS UNDER
   * THEIR CURSOR. `MIN_PASSWORD_LENGTH` is a deliberate second copy of the
   * server's — `settingsModel.ts` and `inviteModel.ts` both hold one, and all of
   * them move if the server's does. ⚠️ NOT TRIMMED: a leading space is a
   * legitimate password character, and trimming here would let an admin hand
   * over a string the account was never given.
   */
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH

  /* 🔴 HALF AN ANSWER IS REFUSED BEFORE THE REQUEST. A subject with no class
     cannot be assigned to anybody — `ClassSubject` is a fact about the PAIR —
     and a class with no subject is what the table below is for. */
  const halfTaught = subject.trim().length > 0 && classId === ''

  const submit = async () => {
    setBusy(true)
    setFailed(null)
    setAddedTeaching(null)
    try {
      const teacher = await addTeacher(
        { name: name.trim(), email: email.trim(), password },
        auth,
      )

      /*
       * ⚠️ THE SECOND STEP RUNS AFTER THE FIRST AND FAILS SEPARATELY, WHICH THE
       * RECEIPT HAS TO SAY. The account exists the moment `addTeacher` returns;
       * an admin told only "that failed" would create the teacher a second time
       * and then be refused for the address being taken.
       */
      if (subject.trim().length > 0 && classId !== '') {
        const klass = live.find((c) => c.id === classId)
        try {
          const subjectId = await subjectIdForName(subject, subjects, auth)
          await putTeacherOnSubject(classId, subjectId, teacher.userId, auth)
          setAddedTeaching(
            t.teacherCreatedWithSubject(subject.trim(), klass?.name ?? classId),
          )
        } catch {
          setAddedTeaching(t.subjectStepFailed)
        }
      } else {
        setAddedTeaching(t.teacherCreatedNoSubject)
      }

      setAdded(teacher)
      setName('')
      setEmail('')
      setPassword('')
      setSubject('')
      setClassId('')
      onAdded()
    } catch (err) {
      /*
       * ⚠️ 400 IS THE TAKEN ADDRESS, AND IT IS THE ONE FAILURE WORTH NAMING.
       * The server answers it specifically — narrow enough not to be a directory
       * of other schools' staff, specific enough that an admin does not retype a
       * colleague's address three times and then call support.
       */
      const status = (err as { status?: number } | null)?.status
      setFailed(status === 400 || status === 409 ? t.emailTaken : t.addFailed)
    } finally {
      setBusy(false)
    }
  }

  /*
   * ✅ A RECEIPT, NOT A CREDENTIAL PANEL. What stood here was an `InvitePanel`
   * rendering a live one-time link, and the form was hidden behind it because
   * *"a visible form under a live invitation is an invitation to create a second
   * teacher while the first one's link is still unhanded-over"*. There is no
   * one-time anything to protect now — the admin chose the password and already
   * has it — so this only has to confirm that the account works and can be
   * signed into today.
   *
   * ⚠️ IT IS ALSO, AS OF THIS COMMIT, REACHABLE. While the form was an inline
   * panel its `onAdded` closed the panel, so this branch was unmounted in the
   * same tick `added` was set and drew exactly zero times. The dialog now stays
   * open on it — which is the only place the SECOND step's outcome is ever
   * reported, and that step fails on its own.
   *
   * ⚠️ IT SITS INSIDE `Panel` LIKE THE FORM DOES rather than drawing its own
   * bordered box inside the dialog's card. Two nested borders on an overlay read
   * as a panel that failed to fill its container.
   */
  if (added !== null) {
    return (
      <Panel raised>
        <div className="flex items-start justify-between gap-3">
          <h3 className="flex items-center gap-2 text-dash-sm font-extrabold text-ink">
            <CheckIcon size={16} />
            {t.teacherCreated(added.name)}
          </h3>
          <CloseButton label={t.close} onClose={onClose} />
        </div>
        <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.teacherCreatedHint(added.email)}</p>
        {/* 🔴 WHETHER THEY CAN OPEN A CLASS YET, WHICH IS THE PART AN ADMIN
            CANNOT SEE FROM ANYWHERE ELSE ON THIS SCREEN. */}
        {addedTeaching !== null && (
          <p className="mt-2 text-dash-xs font-bold text-ink">{addedTeaching}</p>
        )}
        {/*
          ⚠️ TWO EXITS, AND THEY ARE DIFFERENT ANSWERS. "Add another" empties the
          form and stays; "Close" is done. While this was an inline panel there
          was only the first, and the admin who had finished had to go back up to
          the toolbar to put the page away.
        */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <DashButton variant="secondary" onClick={() => setAdded(null)}>
            {t.addAnother}
          </DashButton>
          <DashButton variant="ghost" onClick={onClose}>
            {t.close}
          </DashButton>
        </div>
      </Panel>
    )
  }

  return (
    <Panel raised>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {/*
          🔴 THE ✕ IS A GLYPH, NOT AN ICON FROM THE SET, AND THAT IS DELIBERATE.
          `dashIcons`' `CrossIcon` says in its own note *"NOT A 'CLOSE' ICON …
          this is the mark for a child who is absent"* — borrowing it here would
          put the absence mark on a dismiss control. `✕` is what the tier's other
          dialog already uses.

          ⚠️ IT IS NOT THE ONLY WAY OUT — Escape and pressing outside both close
          this, and `ModalCard` owns both. This is the one a reader can SEE, which
          is the only reason a dialog needs a visible close at all.
        */}
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-dash-md font-extrabold text-ink">{t.addTeacher}</h2>
          <CloseButton label={t.close} onClose={onClose} />
        </div>
        <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.addTeacherHint}</p>

        <div className="mt-4 grid max-w-xl gap-4 sm:grid-cols-2">
          <Field label={t.teacherName} htmlFor="teacher-name" width="full">
            <Input
              id="teacher-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </Field>
          <Field label={t.teacherEmail} htmlFor="teacher-email" width="full">
            <Input
              id="teacher-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
            />
          </Field>
          {/*
            ✅ THE PASSWORD FIELD THAT DELIBERATELY DID NOT EXIST. The comment
            here read: *"there is no password field, and that is the security
            property — the teacher sets their own when they claim the
            invitation"*. True, and it described a flow these schools cannot run:
            it needs the teacher to have a working inbox on a device they have
            with them, within 72 hours, before the product will let them in.

            ⚠️ SO THE ADMIN KNOWS THE FIRST PASSWORD, AND THE HINT UNDER THE BOX
            SAYS SO RATHER THAN LEAVING THEM TO ASSUME OTHERWISE. It is the
            teacher's to change on their own Settings screen the moment they sign
            in, and nothing can read it back afterwards.
          */}
          <Field label={t.teacherPassword} htmlFor="teacher-password" width="full">
            <Input
              id="teacher-password"
              /* ⚠️ `type="text"`, NOT `password`. The admin is about to write
                 this on a slip of paper for somebody else — masking it protects
                 nothing here (they typed it) and guarantees typos in the one
                 string that has to be transcribed exactly. */
              type="text"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={256}
            />
          </Field>

          {/*
            ✅ E4 — THE SUBJECT, AND THE CLASS IT IS TAKEN IN, AS ONE PAIR.
            Neither is a fact on its own: a subject belongs to the school and a
            class to the timetable, and what makes a teacher able to open
            anything is the row that joins the two to a person.

            ⚠️ A TYPED NAME WITH A LIST BEHIND IT, NOT A DROPDOWN ALONE. A school
            in its first week has named no subjects, so a dropdown would be an
            empty control an admin cannot get past; the `datalist` offers what
            already exists and `subjectIdForName` reuses a match rather than
            creating "maths" beside "Maths".
          */}
          <Field label={t.teacherSubject} htmlFor="teacher-subject" width="full">
            <Input
              id="teacher-subject"
              list="school-subject-names"
              autoComplete="off"
              placeholder={t.teacherSubjectPlaceholder}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={64}
            />
          </Field>
          <datalist id="school-subject-names">
            {subjects.map((s) => (
              <option key={s.id} value={s.name} />
            ))}
          </datalist>

          <Field label={t.teacherClass} htmlFor="teacher-class" width="full">
            <Select
              id="teacher-class"
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
            >
              <option value="">{t.noClassYet}</option>
              {live.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="mt-3 max-w-prose text-dash-xs text-muted">{t.teacherSubjectHint}</div>

        {halfTaught && (
          <div className="mt-3 max-w-prose">
            <Note tone="warning" icon={<InfoIcon size={16} />}>
              {t.teacherSubjectClassMissing}
            </Note>
          </div>
        )}

        {tooShort && (
          <div className="mt-3 max-w-prose">
            <Note tone="warning" icon={<InfoIcon size={16} />}>
              {t.passwordTooShort(MIN_PASSWORD_LENGTH)}
            </Note>
          </div>
        )}

        <div className="mt-4">
          <DashButton
            type="submit"
            disabled={
              busy ||
              halfTaught ||
              name.trim().length === 0 ||
              email.trim().length === 0 ||
              password.length < MIN_PASSWORD_LENGTH
            }
          >
            {busy ? t.adding : t.add}
          </DashButton>
        </div>

        {failed !== null && (
          <div className="mt-4">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {failed}
            </Note>
          </div>
        )}
      </form>
    </Panel>
  )
}

/* ─────────────────────────────────────────────────────────────── E4 ── */

/**
 * Who teaches each class.
 *
 * 🔴 IT IS ON THIS SCREEN RATHER THAN ON E1 BECAUSE THE TWO HALVES ARE ONE JOB.
 * An admin arriving here is answering "is the right person in front of the right
 * class", and the answer needs both lists at once — a teacher with no class and
 * a class with no teacher are the same problem read from two ends.
 */
function ClassAssignment({
  lang,
  auth,
  canWrite,
  classes,
  subjects,
  staff,
  onChanged,
}: {
  lang: Lang
  auth?: StaffAuth
  canWrite: boolean
  classes: Resource<ClassSummary[]>
  subjects: readonly SubjectRow[]
  staff: readonly SchoolStaffRow[]
  onChanged: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [busyClassId, setBusyClassId] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  /* Which class's subject-teacher form is open. One at a time: two open forms
     are two half-finished decisions an admin has to hold in their head. */
  const [addingFor, setAddingFor] = useState<string | null>(null)

  /* The same substitution, and the same removal: five invented classes with
     invented class codes stood in for a school that has not made any yet. */
  const classList = classes.data ?? []
  const classSurfaceState: SurfaceState = classes.state

  const teachers = useMemo(() => assignableTeachers(staff), [staff])
  const rows = useMemo(() => assignableClasses(classList), [classList])
  const unassigned = useMemo(() => unassignedClassCount(classList), [classList])

  const assign = async (classId: string, teacherId: string | null) => {
    setBusyClassId(classId)
    setFailed(null)
    try {
      await assignTeacher(classId, teacherId, auth)
      onChanged()
    } catch {
      setFailed(t.assignFailed)
    } finally {
      setBusyClassId(null)
    }
  }

  return (
    <>
      <SectionHead hint={t.assignHint}>{t.assignTitle}</SectionHead>

      {/* 🔴 THE SECOND SENTENCE THIS SECTION NEEDED. The hint above says a class
          with nobody assigned can be opened by nobody; this says what the other
          column does about it, because "subject teacher" reads like a label
          rather than like a permission. */}
      <p className="mb-4 max-w-prose text-dash-xs text-muted">{t.subjectTeacherHint}</p>

      {/* 🔴 A COUNT WITH ITS CONSEQUENCE, NEVER A BARE NUMBER (OD-39/OD-72). */}
      <div className="mb-4">
        <Note
          tone={unassigned > 0 ? 'warning' : 'success'}
          icon={unassigned > 0 ? <AlertIcon size={16} /> : <CheckIcon size={16} />}
        >
          {unassigned > 0 ? t.unassigned(unassigned) : t.unassignedNone}
        </Note>
      </div>

      <DataSurface state={classSurfaceState} skeletonRows={4} errorMessage={describeAdminError(classes.error, lang)}>
        <TableCard>
          <table className="w-full border-collapse">
            <Thead
              headers={[t.className, t.children, t.assignedTo, t.subjectTeachersColumn]}
            />
            <tbody>
              {rows.map((c) => (
                <ClassRow
                  key={c.id}
                  klass={c}
                  lang={lang}
                  auth={auth}
                  canWrite={canWrite}
                  teachers={teachers}
                  subjects={subjects}
                  staff={staff}
                  busy={busyClassId === c.id}
                  adding={addingFor === c.id}
                  onOpenAdd={() => setAddingFor(addingFor === c.id ? null : c.id)}
                  onAssign={(teacherId) => void assign(c.id, teacherId)}
                  onChanged={() => {
                    setAddingFor(null)
                    onChanged()
                  }}
                />
              ))}
            </tbody>
          </table>
        </TableCard>
      </DataSurface>

      {failed !== null && (
        <div className="mt-4">
          <Note tone="warning" icon={<InfoIcon size={16} />}>
            {failed}
          </Note>
        </div>
      )}
    </>
  )
}

/**
 * One class: who runs it, and who else stands in front of it.
 *
 * 🔴 A COMPONENT RATHER THAN MORE JSX IN THE LOOP, because the subject form
 * below holds state — a half-typed subject name and a chosen teacher — and state
 * hoisted to the table would be one draft shared by twelve rows. Opening the
 * form on Class 2B would show what somebody started typing against Class 1A.
 */
function ClassRow({
  klass,
  lang,
  auth,
  canWrite,
  teachers,
  subjects,
  staff,
  busy,
  adding,
  onOpenAdd,
  onAssign,
  onChanged,
}: {
  klass: ClassSummary
  lang: Lang
  auth?: StaffAuth
  canWrite: boolean
  teachers: readonly SchoolStaffRow[]
  subjects: readonly SubjectRow[]
  staff: readonly SchoolStaffRow[]
  busy: boolean
  adding: boolean
  onOpenAdd: () => void
  onAssign: (teacherId: string | null) => void
  onChanged: () => void
}) {
  const t = ADMIN_COPY[lang]
  const c = klass
  const [subject, setSubject] = useState('')
  const [teacherId, setTeacherId] = useState('')
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const taken = useMemo(() => subjectTeachersFor(c.id, staff), [c.id, staff])

  const add = async () => {
    setSaving(true)
    setFailed(null)
    try {
      const subjectId = await subjectIdForName(subject, subjects, auth)
      await putTeacherOnSubject(c.id, subjectId, teacherId, auth)
      setSubject('')
      setTeacherId('')
      onChanged()
    } catch {
      // The status is the diagnosis; the body is never read (`client.ts`).
      setFailed(t.subjectTeacherFailed)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Tr>
        <Td bold>{c.name}</Td>
        <Td num>{c.childCount}</Td>
        <Td>
          {canWrite ? (
            <div className="max-w-[16.25rem]">
              <Select
                aria-label={t.assignedTo}
                value={c.teacher?.id ?? ''}
                disabled={busy}
                onChange={(e) =>
                  onAssign(e.target.value === '' ? null : e.target.value)
                }
              >
                <option value="">{t.nobody}</option>
                {teachers.map((p) => (
                  <option key={p.userId} value={p.userId}>
                    {p.name ?? p.email ?? p.userId}
                  </option>
                ))}
                {c.teacher !== null &&
                  !teachers.some((p) => p.userId === c.teacher?.id) && (
                    <option value={c.teacher.id}>
                      {c.teacher.name ?? c.teacher.id}
                    </option>
                  )}
              </Select>
            </div>
          ) : (
            <span className={c.teacher === null ? 'text-muted' : 'text-ink'}>
              {c.teacher?.name ?? t.nobody}
            </span>
          )}
        </Td>

        {/*
          🔴 WHO ELSE CAN OPEN THIS CLASS. The column beside it names one person;
          this names everybody the school has put in front of the same children,
          and each of them reaches the class through their subject rather than
          through `Class.teacherId`.

          ⚠️ A SUBJECT WITH NO TEACHER OF ITS OWN IS NOT LISTED HERE, AND THAT IS
          NOT AN OMISSION. `ClassSubject.teacherId` null means *"the class
          teacher takes it"* — already answered, in full, by the cell to the
          left. E4's class screen is where a curriculum is read.
        */}
        <Td>
          {taken.length === 0 ? (
            <span className="text-muted">{t.subjectTeacherNoneYet}</span>
          ) : (
            <span className="flex flex-wrap gap-1.5">
              {taken.map((s) => (
                <StatusPill
                  key={`${s.subjectId}-${s.teacherId}`}
                  tone="neutral"
                  icon={<PointIcon size={14} />}
                >
                  {`${s.subjectName} — ${s.teacherName}`}
                </StatusPill>
              ))}
            </span>
          )}

          {canWrite && (
            <div className="mt-2">
              <DashButton variant="ghost" onClick={onOpenAdd}>
                {adding ? t.cancel : t.addSubjectTeacher}
              </DashButton>
            </div>
          )}
        </Td>
      </Tr>

      {/*
        ⚠️ THE FORM SPANS THE ROW RATHER THAN SITTING IN THE CELL — `StaffRow`'s
        rule. A subject box, a teacher list and their two buttons do not fit in a
        table cell, and squeezing them in is how a teacher's name ends up
        truncated in the control that assigns them.
      */}
      {canWrite && adding && (
        <tr>
          <td colSpan={4} className="border-b border-line bg-canvas px-4 py-4">
            <form
              className="max-w-prose rounded-lg border border-line bg-surface p-4"
              onSubmit={(e) => {
                e.preventDefault()
                void add()
              }}
            >
              <h3 className="text-dash-sm font-extrabold text-ink">
                {t.addSubjectTeacher}
              </h3>
              <p className="mt-1 text-dash-xs text-muted">{t.subjectTeacherHint}</p>

              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field
                  label={t.teacherSubject}
                  htmlFor={`subject-${c.id}`}
                  width="full"
                >
                  <Input
                    id={`subject-${c.id}`}
                    list="school-subject-names"
                    autoComplete="off"
                    placeholder={t.teacherSubjectPlaceholder}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={64}
                  />
                </Field>
                <Field
                  label={t.assignedTo}
                  htmlFor={`subject-teacher-${c.id}`}
                  width="full"
                >
                  <Select
                    id={`subject-teacher-${c.id}`}
                    value={teacherId}
                    onChange={(e) => setTeacherId(e.target.value)}
                  >
                    <option value="">{t.chooseTeacher}</option>
                    {/* 🔴 THE SAME LIST THE CLASS-TEACHER DROPDOWN USES, so an
                        admin is never offered a name the server would refuse:
                        active TEACHERs of this school and nobody else. */}
                    {teachers.map((p) => (
                      <option key={p.userId} value={p.userId}>
                        {p.name ?? p.email ?? p.userId}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <DashButton
                  type="submit"
                  disabled={saving || subject.trim().length === 0 || teacherId === ''}
                >
                  {/* ⚠️ `subjectAdd`, NOT `add` — that one reads "Add teacher",
                      which is the other form's sentence and would promise a
                      second account rather than an assignment. */}
                  {saving ? t.saving : t.subjectAdd}
                </DashButton>
                <DashButton variant="ghost" onClick={onOpenAdd}>
                  {t.cancel}
                </DashButton>
              </div>

              {failed !== null && (
                <div className="mt-3">
                  <Note tone="warning" icon={<AlertIcon size={16} />}>
                    {failed}
                  </Note>
                </div>
              )}
            </form>
          </td>
        </tr>
      )}
    </>
  )
}
